/** The cellular automaton: one tick of every awake cell. The hottest code of the game. */
import { EMPTY, FIRE, STEAM, ICE, STATE_POWDER, STATE_LIQUID, STATE_GAS, NO_DECAY, FLAG_CLINGS, FLAG_FLAMMABLE } from './materials';
import { CHANCE_ONE } from './reactions';
import { CHUNK_SHIFT, CHUNK_SIZE, beginChunkStep, wakeRegion, type ChunkGrid } from './chunks';
import { nextInt, nextU32 } from './prng';
import {
  LIQUID_REACH, SLIDE_DEPTH, SLIDE_AFTER_FALL, LIQ_HAS_DIR, LIQ_RIGHT, LIQ_TURNED, LIQ_RESTING,
  wakeRect, type World,
} from './world';

/** Tries the reaction registered for the ordered pair (cell at a, cell at b). Returns true if it fired. */
function react(world: World, a: number, b: number, ax: number, ay: number, bx: number, by: number): boolean {
  const phys = world.physique.data;
  const rx = world.rules.reactions;
  const entry = rx.table[(phys[a] << 8) | phys[b]];
  if (entry === 0) return false;
  // A reactive contact keeps its area awake until it fires, even when the roll fails.
  wakeRect(world, ax < bx ? ax : bx, ay < by ? ay : by, ax > bx ? ax : bx, ay > by ? ay : by);
  const e = entry - 1;
  if (rx.chance[e] < CHANCE_ONE && (nextU32(world.rng) & 0xffff) >= rx.chance[e]) return false;
  phys[a] = rx.outFirst[e];
  phys[b] = rx.outSecond[e];
  world.cellState.data[a] = 0;
  world.cellState.data[b] = 0;
  if (rx.consumeFirst[e] > 0 && (nextU32(world.rng) & 0xffff) < rx.consumeFirst[e]) phys[a] = EMPTY_ID;
  if (rx.consumeSecond[e] > 0 && (nextU32(world.rng) & 0xffff) < rx.consumeSecond[e]) phys[b] = EMPTY_ID;
  if (rx.blast[e] > 0) {
    if (rx.blastAtFirst[e] === 1) world.blasts.push(ax, ay, rx.blast[e]);
    else world.blasts.push(bx, by, rx.blast[e]);
  }
  return true;
}

// Local copies of imported ids: under some module loaders (Vitest), each read of an import goes through a getter.
const EMPTY_ID = EMPTY;
const FIRE_ID = FIRE;
const STEAM_ID = STEAM;
const ICE_ID = ICE;
const POWDER = STATE_POWDER;
const LIQUID = STATE_LIQUID;
const GAS = STATE_GAS;
const NO_DECAY_ID = NO_DECAY;
const CHUNK_SHIFT_LOCAL = CHUNK_SHIFT;
const CHUNK_SIZE_LOCAL = CHUNK_SIZE;
const L_DIR = LIQ_HAS_DIR;
const L_RIGHT = LIQ_RIGHT;
const L_TURNED = LIQ_TURNED;
const L_RESTING = LIQ_RESTING;
const L_UNSETTLE = ~(LIQ_TURNED | LIQ_RESTING) & 0xff;
const REACH = LIQUID_REACH;
const DEPTH = SLIDE_DEPTH;
const AFTER_FALL = SLIDE_AFTER_FALL;

/** Wakes [x0..x1] × [y0..y1] from inside step(), now and for the next tick. */
function wakeInStep(
  grid: ChunkGrid, w: number, h: number, r: number, left: number, right: number, top: number, bottom: number,
  x0: number, y0: number, x1: number, y1: number,
): void {
  if (x0 >= left && x1 <= right && y0 >= top && y1 <= bottom) {
    const cur = grid.cur;
    const next = grid.next;
    if (x0 < cur[r]) cur[r] = x0;
    if (y0 < cur[r + 1]) cur[r + 1] = y0;
    if (x1 > cur[r + 2]) cur[r + 2] = x1;
    if (y1 > cur[r + 3]) cur[r + 3] = y1;
    if (x0 < next[r]) next[r] = x0;
    if (y0 < next[r + 1]) next[r + 1] = y0;
    if (x1 > next[r + 2]) next[r + 2] = x1;
    if (y1 > next[r + 3]) next[r + 3] = y1;
  } else {
    wakeRegion(grid, w, h, x0, y0, x1, y1);
  }
}

/**
 * One liquid cell's move (see LIQUID_SPREAD; a viscous liquid such as lava flows less far per tick). Returns -1 if nothing changed, else the column it moved to
 * (its own column for a fall or a change of flags). The caller wakes the neighbourhood.
 */
function flowLiquid(world: World, x: number, y: number, rowStamp: number, rightward: boolean): number {
  const w = world.physique.w;
  const phys = world.physique.data;
  const cells = world.cellState.data;
  const state = world.rules.materials.state;
  const displace = world.rules.materials.displace;
  const rowIdx = y * w;
  const rowBelow = rowIdx + w;
  const idx = rowIdx + x;
  const m = phys[idx];
  const key = m << 8;
  const belowIdx = rowBelow + x;
  const spread = world.rules.materials.spread[m];
  const flags0 = cells[idx];
  let flags = flags0;
  if ((flags & L_DIR) === 0) flags |= L_DIR | (((x ^ y ^ world.tick) & 1) === 0 ? L_RIGHT : 0);
  const d = (flags & L_RIGHT) !== 0 ? 1 : -1;
  let to = -1;

  if (displace[key | phys[belowIdx]] === 1) {
    to = belowIdx;
  } else if (x + d >= 0 && x + d < w && displace[key | phys[belowIdx + d]] === 1) {
    to = belowIdx + d;
  } else if (x - d >= 0 && x - d < w && displace[key | phys[belowIdx - d]] === 1) {
    to = belowIdx - d;
    flags ^= L_RIGHT;
  }

  if (to >= 0) {
    flags &= L_UNSETTLE; // one level down: free to spread again
  } else {
    if ((flags & L_RESTING) !== 0 && y > 0 && state[phys[idx - w]] === LIQUID &&
        ((x > 0 && displace[key | phys[idx - 1]] === 1) || (x < w - 1 && displace[key | phys[idx + 1]] === 1))) {
      flags &= L_UNSETTLE; // under pressure with room on a side
    }
    if ((flags & L_RESTING) === 0) {
      let dist = 0;
      for (let k = 1; k <= spread; k++) {
        const nx = x + d * k;
        if (nx < 0 || nx >= w || displace[key | phys[rowIdx + nx]] !== 1) break;
        dist = k;
        if (displace[key | phys[rowBelow + nx]] === 1) break; // stop over a hole, fall next tick
      }
      if (dist > 0) {
        to = idx + d * dist;
      } else {
        // Blocked at once. Flowing liquid is not an edge: turn around for free if there is room behind,
        // else wait (a jam changes nothing and may sleep). Anything else is an edge: turn back once, then
        // rest, so a drop crosses a flat floor at most twice and then stays against an edge.
        const nx = x + d;
        let edge = true;
        if (nx >= 0 && nx < w && state[phys[rowIdx + nx]] === LIQUID && (cells[rowIdx + nx] & L_RESTING) === 0) {
          edge = false;
          if (x - d >= 0 && x - d < w && displace[key | phys[idx - d]] === 1) flags ^= L_RIGHT;
        }
        if (edge) flags = (flags & L_TURNED) !== 0 ? flags | L_RESTING : (flags ^ L_RIGHT) | L_TURNED;
      }
    }
  }

  if (to < 0) {
    if (flags === flags0) return -1;
    cells[idx] = flags;
    return x;
  }

  const displaced = phys[to];
  phys[to] = m;
  phys[idx] = displaced;
  cells[to] = flags;
  cells[idx] = 0;
  // Resting row neighbours of the cell it left flow into it. They keep their turned flag, so they stop
  // at the next edge: without it, a partial layer would slosh from wall to wall forever.
  if (x > 0 && idx - 1 !== to && state[phys[idx - 1]] === LIQUID && (cells[idx - 1] & L_RESTING) !== 0) {
    cells[idx - 1] = (cells[idx - 1] & L_TURNED) | L_DIR | L_RIGHT;
  }
  if (x < w - 1 && idx + 1 !== to && state[phys[idx + 1]] === LIQUID && (cells[idx + 1] & L_RESTING) !== 0) {
    cells[idx + 1] = (cells[idx + 1] & L_TURNED) | L_DIR;
  }

  const tx = to >= rowBelow ? to - rowBelow : to - rowIdx;
  if (to < rowBelow && (tx > x) === rightward) world.movedInRow[tx] = rowStamp;
  return tx;
}

/**
 * One gas cell's tick (steam; fire later). A gas with a lifetime draws it on its first tick, counts it down in
 * its state byte and then turns into its decay material (steam -> water: rain). It rises into lighter gases
 * (the empty cell is a gas of density 0): straight up, else diagonally, else it drifts sideways, the side
 * drawn with the world's generator. Returns -1 if nothing changed, else the column it moved to (its own
 * column for a change in place). Marks the destination so it is not moved again in this tick.
 */
function updateGas(world: World, x: number, y: number, stamp: number): number {
  const w = world.physique.w;
  const phys = world.physique.data;
  const cells = world.cellState.data;
  const tables = world.rules.materials;
  const idx = y * w + x;
  const m = phys[idx];
  let changed = false;

  if (world.airless && (m === FIRE_ID || m === STEAM_ID)) {
    // No air: the flame dies (its reactions of this tick already happened), the steam freezes.
    phys[idx] = m === FIRE_ID ? EMPTY_ID : ICE_ID;
    cells[idx] = 0;
    return x;
  }

  // A flame stays on its fuel and burns as long as there is any: it cannot die before passing the fire on. The
  // cell does not change, but x is returned all the same to keep the chunk awake, or the reactions would stop.
  const clings = (tables.flags[m] & FLAG_CLINGS) !== 0;
  if (clings) {
    const fl = tables.flags;
    if ((x > 0 && (fl[phys[idx - 1]] & FLAG_FLAMMABLE) !== 0) || (x < w - 1 && (fl[phys[idx + 1]] & FLAG_FLAMMABLE) !== 0) ||
        (y > 0 && (fl[phys[idx - w]] & FLAG_FLAMMABLE) !== 0) || (y < world.physique.h - 1 && (fl[phys[idx + w]] & FLAG_FLAMMABLE) !== 0)) {
      return x;
    }
  }

  const span = tables.lifeSpan[m];
  if (span > 0) {
    let life = cells[idx];
    if (life === 0) life = tables.lifeMin[m] + nextInt(world.rng, span);
    life--;
    if (life === 0 && tables.decay[m] !== NO_DECAY_ID) {
      phys[idx] = tables.decay[m];
      cells[idx] = 0;
      return x;
    }
    cells[idx] = life;
    changed = true;
  }

  const density = tables.density[m];
  const state = tables.state;
  // The side is drawn with the generator, biased by the wind (6 to 26 chances out of 32 to go right);
  // a strong wind also pushes the gas diagonally even when it could rise straight up.
  const r = nextU32(world.rng);
  // A flame off its fuel flickers: it only moves one tick in two, so it stays a flame, not a rain of sparks.
  if (clings && ((r >>> 9) & 1) === 0) return changed ? x : -1;
  const wind = world.wind;
  const side = (r & 31) < 16 + wind ? 1 : -1;
  const gust = ((r >>> 5) & 15) < (wind < 0 ? -wind : wind);
  let to = -1;
  if (y > 0) {
    const up = idx - w;
    const wd = wind < 0 ? -1 : 1;
    if (gust && x + wd >= 0 && x + wd < w && state[phys[up + wd]] === GAS && tables.density[phys[up + wd]] < density) to = up + wd;
    else if (state[phys[up]] === GAS && tables.density[phys[up]] < density) to = up;
    else if (x + side >= 0 && x + side < w && state[phys[up + side]] === GAS && tables.density[phys[up + side]] < density) to = up + side;
    else if (x - side >= 0 && x - side < w && state[phys[up - side]] === GAS && tables.density[phys[up - side]] < density) to = up - side;
  }
  if (to < 0 && x + side >= 0 && x + side < w && state[phys[idx + side]] === GAS && tables.density[phys[idx + side]] < density) to = idx + side;
  if (to < 0) return changed ? x : -1;

  const life = cells[idx];
  phys[idx] = phys[to];
  cells[idx] = cells[to];
  phys[to] = m;
  cells[to] = life;
  world.gasMoved[to] = stamp;
  return to % w;
}

/**
 * Advances the simulation by one tick and returns the number of cells that changed (0 = stable).
 *
 * Cells are visited bottom-up, alternating left/right with the tick parity, but only inside the chunks'
 * dirty rects. Every change wakes the cells whose decision could depend on it, both for the rest of this
 * tick and for the next one, so the result is identical to scanning the whole grid.
 * The loop body is written inline on purpose: this is the hottest code of the game.
 */
export function step(world: World): number {
  const w = world.physique.w;
  const h = world.physique.h;
  const phys = world.physique.data;
  const cells = world.cellState.data;
  const moved = world.movedInRow;
  const gasMoved = world.gasMoved;
  const gasStamp = world.tick + 1;
  const state = world.rules.materials.state;
  const displace = world.rules.materials.displace;
  const reactive = world.rules.reactions.reactive;
  const noSlide = world.rules.materials.noSlide;
  const rxTable = world.rules.reactions.table;
  const grid = world.chunks;
  beginChunkStep(grid);
  const cur = grid.cur;
  const cols = grid.cols;
  const rightward = (world.tick & 1) === 0;
  const dir = rightward ? 1 : -1;
  const chunkShift = CHUNK_SHIFT_LOCAL;
  const chunkLast = CHUNK_SIZE_LOCAL - 1;
  let changed = 0;

  for (let y = h - 2; y >= 0; y--) {
    const cy = y >> chunkShift;
    const top = cy << chunkShift;
    const bottom = top + chunkLast < h - 1 ? top + chunkLast : h - 1;
    const rowIdx = y * w;
    const rowBelow = rowIdx + w;
    const rowStamp = world.tick * h + y + 1;

    for (let k = 0; k < cols; k++) {
      const cx = rightward ? k : cols - 1 - k;
      const r = (cy * cols + cx) * 4;
      if (y < cur[r + 1] || y > cur[r + 3]) continue;
      const left = cx << chunkShift;
      const right = left + chunkLast < w - 1 ? left + chunkLast : w - 1;

      // Changes in this row segment, woken in one go at the end of the segment (rows above and the next
      // chunks are visited later). Only the cells ahead in this segment need it at once: a liquid's path
      // or free side may be there, so each change extends the scan bound right away (the loop re-reads it).
      let changedMin = 0x7fffffff;
      let changedMax = -1;
      for (let x = rightward ? cur[r] : cur[r + 2]; rightward ? x <= cur[r + 2] : x >= cur[r]; x += dir) {
        const idx = rowIdx + x;
        const m = phys[idx];
        if (m === EMPTY_ID) continue;
        const s = state[m];
        // A liquid that flowed ahead of the scan already moved this tick (a gas: see gasMoved below).
        if (s === LIQUID && moved[x] === rowStamp) continue;

        if (reactive[m] === 1) {
          const key = m << 8;
          if (x + 1 < w && rxTable[key | phys[idx + 1]] !== 0 && react(world, idx, idx + 1, x, y, x + 1, y)) { changed++; continue; }
          if (rxTable[key | phys[rowBelow + x]] !== 0 && react(world, idx, rowBelow + x, x, y, x, y + 1)) { changed++; continue; }
        }

        if (s !== POWDER) {
          let tx: number;
          if (s === LIQUID) tx = flowLiquid(world, x, y, rowStamp, rightward);
          else if (s === GAS) tx = gasMoved[idx] === gasStamp ? -1 : updateGas(world, x, y, gasStamp);
          else continue;
          if (tx < 0) continue;
          changed++;
          const lo = tx < x ? tx : x;
          const hi = tx > x ? tx : x;
          if (lo < changedMin) changedMin = lo;
          if (hi > changedMax) changedMax = hi;
          if (rightward) {
            const reach = hi + REACH + 1 < right ? hi + REACH + 1 : right;
            if (reach > cur[r + 2]) cur[r + 2] = reach;
          } else {
            const reach = lo - REACH - 1 > left ? lo - REACH - 1 : left;
            if (reach < cur[r]) cur[r] = reach;
          }
          continue;
        }
        const key = m << 8;
        const belowIdx = rowBelow + x;
        let to = -1;
        const currentFall = cells[idx];

        if (displace[key | phys[belowIdx]] === 1) {
          to = belowIdx;
        } else {
          if (noSlide[m] === 1) continue; // gravel: steep columns
          if (currentFall <= AFTER_FALL) {
            let same = 0;
            for (let yy = y + 1; yy < h && same < DEPTH; yy++) {
              if (phys[yy * w + x] !== m) break;
              same++;
            }
            if (same < DEPTH) continue;
          }
          const leftFree = x > 0 && displace[key | phys[belowIdx - 1]] === 1;
          const rightFree = x < w - 1 && displace[key | phys[belowIdx + 1]] === 1;
          if (rightward) to = leftFree ? belowIdx - 1 : rightFree ? belowIdx + 1 : -1;
          else to = rightFree ? belowIdx + 1 : leftFree ? belowIdx - 1 : -1;
          if (to < 0) continue;
        }

        const displaced = phys[to];
        phys[to] = m;
        phys[idx] = displaced;
        cells[to] = currentFall > 254 ? 255 : currentFall + 1;
        cells[idx] = 0;
        changed++;
        if (x < changedMin) changedMin = x;
        if (x > changedMax) changedMax = x;
        if (rightward) {
          const reach = x + REACH + 1 < right ? x + REACH + 1 : right;
          if (reach > cur[r + 2]) cur[r + 2] = reach;
        } else {
          const reach = x - REACH - 1 > left ? x - REACH - 1 : left;
          if (reach < cur[r]) cur[r] = reach;
        }
      }

      if (changedMax >= 0) {
        // Sources and destinations (same row or the one below), plus the cells whose rules look at them.
        wakeInStep(
          grid, w, h, r, left, right, top, bottom,
          changedMin - REACH - 1, y - DEPTH, changedMax + REACH + 1, y + 2,
        );
      }
    }
  }

  world.tick++;
  return changed;
}
